import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CloseIcon from '@material-ui/icons/Close';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import MobileLayout from '../MobileLayout';
import {
  FulfillmentLineExceptionType,
  WaveItem,
  WaveResponse,
} from '../../../services/fulfillment/types';
import {
  cancelWave,
  completeWave,
  getActiveWave,
  reportPickException,
  scanPick,
  scanQc,
  validateCartBin,
} from '../../../services/fulfillment/waveApi';
import { derivePickSegments, PickSegment } from './pickFlowUtils';

const EXCEPTION_LABELS: Record<FulfillmentLineExceptionType, string> = {
  item_not_found: 'Item Not Found',
  item_damaged: 'Item Damaged',
  skip: 'Skip',
  other: 'Other',
};

const isBikeSku = (sku: string) => sku.charAt(0).toUpperCase() === 'B';
const isFrameSku = (sku: string) => sku.charAt(0).toUpperCase() === 'F';
const isWheelSku = (sku: string) => sku.charAt(0).toUpperCase() === 'W';
const isPaaSku = (sku: string) => !isBikeSku(sku) && !isFrameSku(sku) && !isWheelSku(sku);

const itemTypePriority = (item: WaveItem): number => {
  const first = item.sku.charAt(0).toUpperCase();
  if (first === 'F') return 2;
  if (first === 'W') return 3;
  if (first === 'B') return 4;
  return 1;
};

const isDoneItem = (i: WaveItem) =>
  i.waveOrderStatus === 'picked' ||
  i.waveOrderStatus === 'cancelled' ||
  ['quality-check', 'picked', 'packed', 'exception'].includes(i.lineStatus ?? '');

const getCarryMessage = (orderItems: WaveItem[], bagBinCode: string | null): string | null => {
  const hasPaa = orderItems.some(i => isPaaSku(i.sku));
  if (!hasPaa) return null;
  const bagPart = bagBinCode ? `bag ${bagBinCode}` : 'PA&A bag';
  if (orderItems.some(i => isFrameSku(i.sku))) return `Put ${bagPart} with the frame`;
  if (orderItems.some(i => isWheelSku(i.sku))) return `Put ${bagPart} with the wheel`;
  return `Put ${bagPart} with the bike`;
};

type PickStep =
  | { name: 'loading' }
  | { name: 'scan_cart'; orderName: string; customerName: string }
  | {
      name: 'pick_item';
      item: WaveItem;
      itemIndex: number;
      totalItems: number;
      segment: PickSegment;
      segmentIndex: number;
      totalSegments: number;
      scansCompleted: number;
      isPaa: boolean;
    }
  | {
      name: 'move_to_bin';
      item: WaveItem;
      itemIndex: number;
      totalItems: number;
      segment: PickSegment;
      segmentIndex: number;
      totalSegments: number;
    }
  | { name: 'auto_exception'; item: WaveItem; itemIndex: number; totalItems: number }
  | { name: 'paa_confirm'; cartBinCode: string; orderName: string }
  | { name: 'order_complete'; orderName: string }
  | { name: 'wave_done'; completedOrders: number; exceptionCount: number };

interface LocationState {
  wave?: WaveResponse;
}

const MixedWavePickFlow: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { waveId } = useParams<{ waveId: string }>();

  const [step, setStep] = useState<PickStep>({ name: 'loading' });
  const [wave, setWave] = useState<WaveResponse | null>(location.state?.wave ?? null);
  const [cartBinCode, setCartBinCode] = useState<string | null>(null);
  const [scanInput, setScanInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const initDone = useRef(false);
  const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
  const [
    selectedExceptionType,
    setSelectedExceptionType,
  ] = useState<FulfillmentLineExceptionType | null>(null);
  const [exceptionNotes, setExceptionNotes] = useState('');
  const [submittingException, setSubmittingException] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [skippedLineItemGids, setSkippedLineItemGids] = useState<Set<string>>(new Set());
  const [altLocationBinCode, setAltLocationBinCode] = useState<string | null>(null);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const deriveStep = useCallback(
    (w: WaveResponse): PickStep => {
      const exceptionCount = w.items.filter(i => i.lineStatus === 'exception').length;
      const incompleteOrderIds = Array.from(
        new Set(
          w.items
            .filter(i => i.waveOrderStatus !== 'picked' && i.waveOrderStatus !== 'cancelled')
            .map(i => i.waveOrderId),
        ),
      );
      if (incompleteOrderIds.length === 0) {
        return { name: 'wave_done', completedOrders: w.completedOrders, exceptionCount };
      }

      const currentOrderId = incompleteOrderIds[0];
      const orderItems = w.items.filter(i => i.waveOrderId === currentOrderId);
      const unpicked = orderItems
        .filter(i => !isDoneItem(i))
        .sort((a, b) => {
          const aSkipped = skippedLineItemGids.has(a.shopifyLineItemGid);
          const bSkipped = skippedLineItemGids.has(b.shopifyLineItemGid);
          if (aSkipped !== bSkipped) return aSkipped ? 1 : -1;
          return itemTypePriority(a) - itemTypePriority(b);
        });

      if (unpicked.length === 0) {
        // All items for this order are done but waveOrder isn't cancelled yet — show
        // order_complete so the picker explicitly advances rather than jumping to wave_done
        // and potentially skipping remaining orders. Real fix is S-3 on the server.
        return { name: 'order_complete', orderName: orderItems[0]?.shopifyOrderName ?? '' };
      }

      const item = unpicked[0];
      const sortedAll = [...orderItems].sort((a, b) => itemTypePriority(a) - itemTypePriority(b));
      const itemIndex =
        sortedAll.findIndex(i => i.shopifyLineItemGid === item.shopifyLineItemGid) + 1;
      const segments = derivePickSegments(item);
      if (segments.length === 0) {
        return { name: 'auto_exception', item, itemIndex, totalItems: orderItems.length };
      }
      return {
        name: 'pick_item',
        item,
        itemIndex,
        totalItems: orderItems.length,
        segment: segments[0],
        segmentIndex: 0,
        totalSegments: segments.length,
        scansCompleted: 0,
        isPaa: isPaaSku(item.sku),
      };
    },
    [skippedLineItemGids],
  );

  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;
    const loadWave = (w: WaveResponse) => {
      setWave(w);
      const firstStep = deriveStep(w);
      if (firstStep.name === 'pick_item' && firstStep.isPaa) {
        setStep({
          name: 'scan_cart',
          orderName: firstStep.item.shopifyOrderName,
          customerName: firstStep.item.customerName,
        });
      } else if (firstStep.name === 'auto_exception') {
        const orderHasPickablePaa = w.items
          .filter(i => i.waveOrderId === firstStep.item.waveOrderId && !isDoneItem(i))
          .some(i => isPaaSku(i.sku) && derivePickSegments(i).length > 0);
        setStep(
          orderHasPickablePaa
            ? {
                name: 'scan_cart',
                orderName: firstStep.item.shopifyOrderName,
                customerName: firstStep.item.customerName,
              }
            : firstStep,
        );
      } else {
        setStep(firstStep);
      }
    };
    if (wave) {
      loadWave(wave);
      return;
    }
    getActiveWave()
      .then(w => {
        if (w) {
          loadWave(w);
        } else {
          history.push('/mobile/pick');
        }
      })
      .catch(() => history.push('/mobile/pick'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only

  // Re-derive step when skip list changes
  useEffect(() => {
    if (wave && step.name === 'pick_item') {
      setStep(deriveStep(wave));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skippedLineItemGids]);

  // Reset alternate location when item or segment changes
  const currentLineItemGid = step.name === 'pick_item' ? step.item.shopifyLineItemGid : null;
  useEffect(() => {
    setAltLocationBinCode(null);
  }, [currentLineItemGid]);

  const focusInput = () => {
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Order-start routing: reset the bag, then show scan_cart if the order has pickable
  // PA&A items, or go straight to the first step otherwise. Used by handleNextOrder and
  // any exception handler that crosses an order boundary.
  const beginOrderTransition = (nextStep: PickStep, w: WaveResponse) => {
    setCartBinCode(null);
    if (nextStep.name === 'pick_item' && nextStep.isPaa) {
      setStep({
        name: 'scan_cart',
        orderName: nextStep.item.shopifyOrderName,
        customerName: nextStep.item.customerName,
      });
    } else if (nextStep.name === 'auto_exception') {
      // Exception is first in sort order, but other pickable PA&A items may still need a bag
      const orderHasPickablePaa = w.items
        .filter(i => i.waveOrderId === nextStep.item.waveOrderId && !isDoneItem(i))
        .some(i => isPaaSku(i.sku) && derivePickSegments(i).length > 0);
      setStep(
        orderHasPickablePaa
          ? {
              name: 'scan_cart',
              orderName: nextStep.item.shopifyOrderName,
              customerName: nextStep.item.customerName,
            }
          : nextStep,
      );
    } else {
      setStep(nextStep);
      if (nextStep.name === 'pick_item') focusInput();
    }
  };

  const handleAutoException = async () => {
    if (step.name !== 'auto_exception') return;
    const { item } = step;
    setSubmittingException(true);
    const result = await reportPickException(
      Number(waveId),
      item.shopifyLineItemGid,
      'item_not_found',
      'No pickable inventory location',
    );
    setSubmittingException(false);
    if ('error' in result) {
      setScanError(`Failed to report exception for ${item.sku} — please try again`);
      return;
    }
    const updatedWave = result as WaveResponse;
    setWave(updatedWave);
    const nextStep = deriveStep(updatedWave);
    if (
      (nextStep.name === 'pick_item' || nextStep.name === 'auto_exception') &&
      nextStep.item.waveOrderId !== item.waveOrderId
    ) {
      beginOrderTransition(nextStep, updatedWave);
    } else {
      setStep(nextStep);
      if (nextStep.name === 'pick_item') focusInput();
    }
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const doSilentScanQc = async (
    lineItemGid: string,
    cartBin: string,
    fromBin: string,
    qty: number,
    isFinal: boolean,
    sku: string,
  ): Promise<WaveResponse | null | 'invalid_bin'> => {
    const attempt = async () => {
      try {
        return await scanQc(Number(waveId), lineItemGid, cartBin, fromBin, qty, isFinal);
      } catch {
        return { error: 'network error' } as { error: string };
      }
    };
    let result = await attempt();
    if ('error' in result && (result as { error: string }).error === 'invalid_bin') {
      setScanError('Bag bin is invalid — please re-scan the bag for this order');
      return 'invalid_bin';
    }
    if ('error' in result) result = await attempt();
    if ('error' in result) {
      setScanError(`Failed to record pick for ${sku} — reporting exception`);
      try {
        await reportPickException(
          Number(waveId),
          lineItemGid,
          'other',
          `Auto: failed to move ${qty} of ${sku} from ${fromBin} to ${cartBin} after 1 retry: ${
            (result as { error: string }).error
          }`,
        );
      } catch (err) {
        console.error('reportPickException also failed:', err);
      }
      return null;
    }
    const waveResult = result as WaveResponse;
    if (waveResult.inventoryWarning) {
      setScanError(
        `Inventory warning for ${sku}: ${waveResult.inventoryWarning} — supervisor follow-up needed`,
      );
    }
    return waveResult;
  };

  const handleCartScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const value = scanInput.trim();
    setScanInput('');
    if (!value) return focusInput();
    setScanError(null);
    setScanning(true);
    const validation = await validateCartBin(value);
    setScanning(false);
    if ('error' in validation) {
      setScanError('Invalid bag bin — please scan a valid staging bin');
      focusInput();
      return;
    }
    setCartBinCode(value);
    if (!wave) return;
    const nextStep = deriveStep(wave);
    setStep(nextStep);
    if (nextStep.name === 'pick_item') focusInput();
  };

  const handlePickScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || step.name !== 'pick_item') return;
    if (!wave) return;
    if (step.isPaa && !cartBinCode) return;
    const value = scanInput.trim();
    setScanInput('');
    setScanError(null);
    if (!value) return focusInput();

    if (step.scansCompleted === 0) {
      setScanning(true);
      const result = await scanPick(Number(waveId), step.item.shopifyLineItemGid, value);
      setScanning(false);
      if ('error' in result && result.error === 'sku_mismatch') {
        setScanError('SKU does not match. Please scan the correct item.');
        triggerShake();
        focusInput();
        return;
      }
    } else {
      if (value.toUpperCase() !== step.item.sku.toUpperCase()) {
        setScanError('SKU does not match. Please scan the correct item.');
        triggerShake();
        focusInput();
        return;
      }
    }

    const newScansCompleted = step.scansCompleted + 1;
    if (newScansCompleted < step.segment.quantity) {
      setStep({ ...step, scansCompleted: newScansCompleted });
      focusInput();
      return;
    }

    // Segment complete
    if (step.isPaa) {
      if (!cartBinCode) return;
      const isFinalSegment = step.segmentIndex === step.totalSegments - 1;
      const fromBin = altLocationBinCode ?? step.segment.binCode;

      setScanning(true);
      const updatedWave = await doSilentScanQc(
        step.item.shopifyLineItemGid,
        cartBinCode,
        fromBin,
        step.segment.quantity,
        isFinalSegment,
        step.item.sku,
      );
      setScanning(false);

      if (updatedWave === 'invalid_bin') {
        setCartBinCode(null);
        setStep({
          name: 'scan_cart',
          orderName: step.item.shopifyOrderName,
          customerName: step.item.customerName,
        });
        return;
      }

      if (updatedWave) setWave(updatedWave);

      if (!isFinalSegment) {
        const segments = derivePickSegments(step.item);
        setStep({
          name: 'pick_item',
          item: step.item,
          itemIndex: step.itemIndex,
          totalItems: step.totalItems,
          segment: segments[step.segmentIndex + 1],
          segmentIndex: step.segmentIndex + 1,
          totalSegments: step.totalSegments,
          scansCompleted: 0,
          isPaa: true,
        });
        focusInput();
        return;
      }

      // Final segment of PA&A item — check what comes next for this order
      const effectiveWave = updatedWave ?? wave;
      const nextStep = deriveStep(effectiveWave);
      if (
        nextStep.name === 'pick_item' &&
        !nextStep.isPaa &&
        nextStep.item.waveOrderId === step.item.waveOrderId
      ) {
        // Transitioning to bikes for same order — show paa_confirm
        setStep({
          name: 'paa_confirm',
          cartBinCode: cartBinCode,
          orderName: step.item.shopifyOrderName,
        });
      } else {
        setStep(nextStep);
        if (nextStep.name === 'pick_item') focusInput();
      }
    } else {
      // Bike/frame/wheel — show move_to_bin for bin scan
      setStep({
        name: 'move_to_bin',
        item: step.item,
        itemIndex: step.itemIndex,
        totalItems: step.totalItems,
        segment: step.segment,
        segmentIndex: step.segmentIndex,
        totalSegments: step.totalSegments,
      });
      focusInput();
    }
  };

  const handleBinScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || step.name !== 'move_to_bin') return;
    const value = scanInput.trim();
    setScanInput('');
    setScanError(null);
    if (!value) return focusInput();

    const isFinalSegment = step.segmentIndex === step.totalSegments - 1;

    setScanning(true);
    const result = await scanQc(
      Number(waveId),
      step.item.shopifyLineItemGid,
      value,
      altLocationBinCode ?? step.segment.binCode,
      step.segment.quantity,
      isFinalSegment,
    );
    setScanning(false);
    if ('error' in result) {
      setScanError(
        isBikeSku(step.item.sku)
          ? wave?.qcAllowRefurbishedZone
            ? 'Invalid QC bin. Please scan a valid operations or refurbished-goods bin.'
            : 'Invalid QC bin. Please scan a valid operations bin.'
          : 'Invalid bin. Please scan a valid staging bin.',
      );
      focusInput();
      return;
    }
    const updatedWave = result as WaveResponse;

    if (!isFinalSegment) {
      const segments = derivePickSegments(step.item);
      const nextIdx = step.segmentIndex + 1;
      setWave(updatedWave);
      setStep({
        name: 'pick_item',
        item: step.item,
        itemIndex: step.itemIndex,
        totalItems: step.totalItems,
        segment: segments[nextIdx],
        segmentIndex: nextIdx,
        totalSegments: step.totalSegments,
        scansCompleted: 0,
        isPaa: false,
      });
      setScanInput('');
      focusInput();
      return;
    }

    setWave(updatedWave);
    setScanInput('');

    const currentItem = step.item;
    const waveOrderPicked = updatedWave.items.some(
      i => i.waveOrderId === currentItem.waveOrderId && i.waveOrderStatus === 'picked',
    );
    if (waveOrderPicked) {
      setStep({ name: 'order_complete', orderName: currentItem.shopifyOrderName });
    } else {
      const nextStep = deriveStep(updatedWave);
      setStep(nextStep);
      if (nextStep.name === 'pick_item') focusInput();
    }
  };

  const handlePaaConfirm = () => {
    if (!wave) return;
    const nextStep = deriveStep(wave);
    setStep(nextStep);
    if (nextStep.name === 'pick_item') focusInput();
  };

  const handleSkip = () => {
    if (step.name !== 'pick_item' || !wave) return;
    const orderItems = wave.items.filter(i => i.waveOrderId === step.item.waveOrderId);
    const unpickedCount = orderItems.filter(i => !isDoneItem(i)).length;
    if (step.scansCompleted > 0 || step.segmentIndex > 0 || unpickedCount <= 1) return;
    // Re-derive with new skip set happens via the skippedLineItemGids effect
    setSkippedLineItemGids(prev => new Set(Array.from(prev).concat(step.item.shopifyLineItemGid)));
  };

  const handleNextOrder = () => {
    if (!wave) return;
    setScanError(null);
    beginOrderTransition(deriveStep(wave), wave);
  };

  const handleOpenException = () => {
    setSelectedExceptionType(null);
    setExceptionNotes('');
    setExceptionModalOpen(true);
  };

  const handleSubmitException = async () => {
    if (step.name !== 'pick_item' || !selectedExceptionType) return;
    setSubmittingException(true);
    const result = await reportPickException(
      Number(waveId),
      step.item.shopifyLineItemGid,
      selectedExceptionType,
      exceptionNotes || null,
    );
    setSubmittingException(false);
    if ('error' in result) {
      setScanError('Failed to submit exception. Please try again.');
      setExceptionModalOpen(false);
      return;
    }
    const updatedWave = result as WaveResponse;
    setWave(updatedWave);
    setExceptionModalOpen(false);
    setScanError(null);
    const nextStep = deriveStep(updatedWave);
    if (
      (nextStep.name === 'pick_item' || nextStep.name === 'auto_exception') &&
      nextStep.item.waveOrderId !== step.item.waveOrderId
    ) {
      beginOrderTransition(nextStep, updatedWave);
    } else if (
      step.isPaa &&
      cartBinCode &&
      nextStep.name === 'pick_item' &&
      !nextStep.isPaa &&
      nextStep.item.waveOrderId === step.item.waveOrderId
    ) {
      setStep({
        name: 'paa_confirm',
        cartBinCode: cartBinCode,
        orderName: step.item.shopifyOrderName,
      });
    } else {
      setStep(nextStep);
      if (nextStep.name === 'pick_item') focusInput();
    }
  };

  const handleReleaseRemaining = async () => {
    setReleasing(true);
    try {
      await cancelWave(Number(waveId));
      history.push('/mobile/pick');
    } catch {
      setReleasing(false);
      setReleaseError('Failed to release wave. Please try again.');
    }
  };

  const handleWaveDone = async () => {
    try {
      await completeWave(Number(waveId));
    } catch {
      // best-effort
    }
    history.push('/mobile/pick');
  };

  const renderOrderCard = (item: WaveItem) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          Order
        </Typography>
        <Typography variant="h6">{item.shopifyOrderName}</Typography>
        <Typography variant="body2">{item.customerName}</Typography>
      </CardContent>
    </Card>
  );

  const renderItemCard = (
    item: WaveItem,
    itemIndex: number,
    totalItems: number,
    segmentBinCode: string | null,
    segmentIndex?: number,
    totalSegments?: number,
    scansCompleted?: number,
    segmentQuantity?: number,
    onSkip?: () => void,
    skipDisabled?: boolean,
    onException?: () => void,
  ) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={0.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {itemIndex} of {totalItems} Items
            </Typography>
            {onSkip && (
              <IconButton
                size="small"
                onClick={onSkip}
                disabled={skipDisabled}
                sx={{ p: 0.5, mr: 0.5 }}
                aria-label="Skip item"
              >
                <SkipNextIcon style={{ fontSize: 28, color: skipDisabled ? '#ccc' : '#AAAAAA' }} />
              </IconButton>
            )}
          </Box>
          <Box display="flex" gap={0.5}>
            {onException && (
              <IconButton
                size="small"
                onClick={onException}
                sx={{ p: 0.5, ml: 1 }}
                aria-label="Report issue"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="28px"
                  viewBox="0 0 24 24"
                  width="28px"
                  fill="#AAAAAA"
                  style={{ flexShrink: 0 }}
                >
                  <g>
                    <rect fill="none" height="24" width="24" />
                    <path d="M2.81,2.81L1.39,4.22L7,9.83l0,4.3l-2.6-1.48c-0.17-0.09-0.34-0.14-0.54-0.14c-0.26,0-0.5,0.09-0.7,0.26L2,13.88l6.8,7.18 c0.57,0.6,1.35,0.94,2.18,0.94H17c0.62,0,1.18-0.19,1.66-0.52l1.12,1.12l1.41-1.41L2.81,2.81z M17,20h-6 c-0.39,0-0.64-0.23-0.75-0.36L6.87,16H9l0-4.17l8.14,8.14C17.09,19.98,17.05,20,17,20z M13.83,11H14V3.25C14,2.56,14.56,2,15.25,2 s1.25,0.56,1.25,1.25V11h1V5.25C17.5,4.56,18.06,4,18.75,4S20,4.56,20,5.25v11.92l-2-2V13h-2.17L13.83,11z M13,10.17V2.25 C13,1.56,12.44,1,11.75,1S10.5,1.56,10.5,2.25v5.42L13,10.17z M9.5,6.67V4.25C9.5,3.56,8.94,3,8.25,3c-0.67,0-1.2,0.53-1.24,1.18v0 L9.5,6.67z" />
                  </g>
                </svg>
              </IconButton>
            )}
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography variant="h5" fontWeight="bold">
            {item.sku}
          </Typography>
          <Typography variant="h5" color="primary">
            {altLocationBinCode ?? segmentBinCode ?? item.binLocations[0]?.binCode ?? '—'}
          </Typography>
        </Box>
        {item.imageUrl && (
          <Box mt={1} display="flex" justifyContent="center">
            <img
              src={item.imageUrl}
              alt={item.title ?? item.sku}
              style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain' }}
            />
          </Box>
        )}
        {item.title && (
          <Typography variant="body1" color="text.secondary" mt={1}>
            {item.title}
          </Typography>
        )}
        {totalSegments && totalSegments > 1 && (
          <Typography variant="h6" color="text.secondary" mt={1} display="block">
            Location {(segmentIndex ?? 0) + 1} of {totalSegments}
          </Typography>
        )}
        {(segmentQuantity ?? 1) > 1 && (
          <Typography variant="h6" color="primary" mt={0.5} display="block">
            {scansCompleted ?? 0} of {segmentQuantity} scanned at this location
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderExceptionModal = (item: WaveItem) => {
    const currentBinCode = altLocationBinCode ?? item.binLocations[0]?.binCode;
    const otherLocations = item.binLocations.filter(l => l.binCode !== currentBinCode);

    return (
      <Dialog
        fullScreen
        disableRestoreFocus
        open={exceptionModalOpen}
        onClose={() => setExceptionModalOpen(false)}
      >
        <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setExceptionModalOpen(false)}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Report an Issue
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {item.sku} — {item.shopifyOrderName}
          </Typography>
          <Typography variant="subtitle2" mb={1}>
            What&apos;s the issue?
          </Typography>
          {(Object.keys(EXCEPTION_LABELS) as FulfillmentLineExceptionType[]).map(type => (
            <Button
              key={type}
              variant={selectedExceptionType === type ? 'contained' : 'outlined'}
              color={selectedExceptionType === type ? 'error' : 'inherit'}
              fullWidth
              sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
              onClick={() => setSelectedExceptionType(type)}
            >
              {EXCEPTION_LABELS[type]}
            </Button>
          ))}
          {['item_not_found', 'item_damaged'].includes(selectedExceptionType ?? '') &&
            otherLocations.length > 0 && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="subtitle2" mb={1}>
                  Try a different location:
                </Typography>
                {otherLocations.map(loc => (
                  <Button
                    key={loc.binCode}
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => {
                      setAltLocationBinCode(loc.binCode);
                      setExceptionModalOpen(false);
                      focusInput();
                    }}
                  >
                    {loc.binCode} (qty {loc.quantity})
                  </Button>
                ))}
              </Box>
            )}
          <TextField
            label="Notes (optional)"
            multiline
            rows={3}
            fullWidth
            value={exceptionNotes}
            onChange={e => setExceptionNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={!selectedExceptionType || submittingException}
            onClick={handleSubmitException}
            sx={{ mt: 3 }}
          >
            {submittingException ? 'Submitting…' : 'Submit Exception'}
          </Button>
        </Box>
      </Dialog>
    );
  };

  const renderScanInput = (
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    label: string,
  ) => (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'white',
        borderTop: '1px solid #ddd',
        p: 2,
        maxWidth: 480,
        margin: '0 auto',
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {label}
      </Typography>
      <input
        ref={inputRef}
        value={scanInput}
        onChange={e => setScanInput(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus
        style={{
          width: '100%',
          fontSize: 18,
          padding: '10px 12px',
          border: shake ? '2px solid red' : '2px solid #1976d2',
          borderRadius: 6,
          outline: 'none',
          boxSizing: 'border-box',
          animation: shake ? 'shake 0.4s ease' : 'none',
        }}
        placeholder="Scan barcode..."
      />
      {scanError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {scanError}
        </Alert>
      )}
    </Box>
  );

  const scanningOverlay = scanning && (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        bgcolor: 'rgba(255,255,255,0.85)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress size={56} />
      <Typography variant="h6" color="text.secondary">
        Processing…
      </Typography>
    </Box>
  );

  if (step.name === 'loading') {
    return (
      <MobileLayout>
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </MobileLayout>
    );
  }

  if (step.name === 'auto_exception') {
    return (
      <MobileLayout>
        <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2} px={2}>
          {scanError && <Alert severity="error">{scanError}</Alert>}
          <Alert severity="warning" sx={{ width: '100%' }}>
            No pickable location found for <strong>{step.item.sku}</strong>. This item will be
            marked as an exception and removed from the wave.
          </Alert>
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={submittingException}
            onClick={handleAutoException}
          >
            {submittingException ? 'Reporting…' : 'Report Exception & Continue'}
          </Button>
        </Box>
      </MobileLayout>
    );
  }

  if (step.name === 'scan_cart') {
    return (
      <MobileLayout>
        <Box pb={14} pt={1}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Order
              </Typography>
              <Typography variant="h6">{step.orderName}</Typography>
              <Typography variant="body2">{step.customerName}</Typography>
            </CardContent>
          </Card>
          <Typography variant="h5" mb={1}>
            Scan the bag for this order&apos;s accessories
          </Typography>
        </Box>
        {renderScanInput(handleCartScan, 'Scan Bag Bin')}
      </MobileLayout>
    );
  }

  if (step.name === 'pick_item') {
    const orderItems = wave?.items.filter(i => i.waveOrderId === step.item.waveOrderId) ?? [];
    const unpickedCount = orderItems.filter(i => !isDoneItem(i)).length;
    const isSkipDisabled = step.scansCompleted > 0 || step.segmentIndex > 0 || unpickedCount <= 1;
    return (
      <MobileLayout>
        {scanningOverlay}
        <Box pb={14} pt={1}>
          {renderOrderCard(step.item)}
          {renderItemCard(
            step.item,
            step.itemIndex,
            step.totalItems,
            step.segment.binCode,
            step.segmentIndex,
            step.totalSegments,
            step.scansCompleted,
            step.segment.quantity,
            handleSkip,
            isSkipDisabled,
            handleOpenException,
          )}
        </Box>
        {renderScanInput(handlePickScan, 'Scan to Pick')}
        {renderExceptionModal(step.item)}
      </MobileLayout>
    );
  }

  if (step.name === 'move_to_bin') {
    const isBike = isBikeSku(step.item.sku);
    const orderItems = wave?.items.filter(i => i.waveOrderId === step.item.waveOrderId) ?? [];
    const carryMessage = getCarryMessage(orderItems, cartBinCode);
    return (
      <MobileLayout>
        {scanningOverlay}
        <Box pb={14} pt={1}>
          {renderOrderCard(step.item)}
          {carryMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{carryMessage}</strong>
            </Alert>
          )}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {step.itemIndex} of {step.totalItems} Items
              </Typography>
              {step.totalSegments > 1 && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  Location {step.segmentIndex + 1} of {step.totalSegments}
                </Typography>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography variant="h5" fontWeight="bold">
                  {step.item.sku}
                </Typography>
                <Typography variant="h6" color="secondary">
                  {isBike ? 'Move to QC' : 'Move to Staging'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
        {renderScanInput(handleBinScan, isBike ? 'Scan to QC Location' : 'Scan to Staging Bin')}
      </MobileLayout>
    );
  }

  if (step.name === 'paa_confirm') {
    return (
      <MobileLayout>
        <Box display="flex" flexDirection="column" alignItems="center" pt={4} pb={4} px={2}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={0.5}>
            Accessories in bag {step.cartBinCode}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={1} textAlign="center">
            All accessories for {step.orderName} are in the bag.
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4} textAlign="center">
            Pick up the bag — you&apos;ll put it with the bike.
          </Typography>
          <Button variant="contained" fullWidth onClick={handlePaaConfirm}>
            Continue to Bike
          </Button>
        </Box>
      </MobileLayout>
    );
  }

  if (step.name === 'order_complete') {
    return (
      <MobileLayout>
        <Box display="flex" flexDirection="column" alignItems="center" pt={4} pb={4}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={1}>
            Order Complete
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            {step.orderName}
          </Typography>
          <Button variant="contained" fullWidth sx={{ mb: 2 }} onClick={handleNextOrder}>
            Next Order
          </Button>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => {
              setReleaseError(null);
              setReleaseConfirmOpen(true);
            }}
          >
            Release Remaining Orders
          </Button>
        </Box>
        <Dialog
          open={releaseConfirmOpen}
          onClose={() => {
            if (!releasing) setReleaseConfirmOpen(false);
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Release this wave?
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              This cancels the entire wave and releases all remaining orders back to the queue.
              Items already moved to QC or staging stay where they are. This can&apos;t be undone.
            </Typography>
            {releaseError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {releaseError}
              </Alert>
            )}
            <Button
              variant="contained"
              fullWidth
              sx={{ mb: 1 }}
              disabled={releasing}
              onClick={() => setReleaseConfirmOpen(false)}
            >
              Keep Picking
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              disabled={releasing}
              onClick={handleReleaseRemaining}
            >
              {releasing ? 'Releasing…' : 'Release Wave'}
            </Button>
          </Box>
        </Dialog>
      </MobileLayout>
    );
  }

  // wave_done
  const hasExceptions = step.exceptionCount > 0;
  return (
    <MobileLayout>
      <Box display="flex" flexDirection="column" alignItems="center" pt={4}>
        {hasExceptions ? (
          <ErrorOutlineIcon style={{ fontSize: 80, color: '#ff9800' }} />
        ) : (
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
        )}
        <Typography variant="h5" mt={2} mb={1}>
          {hasExceptions ? 'Wave Complete' : 'All Mixed Orders Picked!'}
        </Typography>
        {hasExceptions && (
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            {step.exceptionCount} item{step.exceptionCount > 1 ? 's' : ''} could not be picked —
            exceptions reported
          </Alert>
        )}
        <Card sx={{ width: '100%', mb: 3 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{step.completedOrders}</Typography>
            <Typography variant="body2" color="text.secondary">
              Orders Processed
            </Typography>
          </CardContent>
        </Card>
        <Button variant="contained" fullWidth onClick={handleWaveDone}>
          Return Home
        </Button>
      </Box>
    </MobileLayout>
  );
};

export default MixedWavePickFlow;
